import { useRef, useState } from "react";
import { IErrorPersonAlreadyConnectedToManagerInfo } from "../../../interfaces/error/IAlreadyAttachPersonToManagerData";
import { IFixPersonToManager } from "../../../interfaces/store/slices/listAgentSlice";
import { useSubmitReattachToManager } from "./useReattachManager";
import { useAppSelector } from "../../../store/reduxHooks";
import { useDispatch } from "react-redux";
import { attachPersonToManager } from "../../../store/slice/adminManagerSlice/adminManagerSlice";
import { FormProps, message } from "antd";
import { useAttachToManagerMutation } from "../../../store/api/listManager.api";
import { isIAlreadyConnectedPersonManagerInfo } from "../../utilities/typeGuards/isIAlreadyConnectedPersonManagerInfo";
import { isIErrorPersonAlreadyConnectedToManagerInfo } from "../../utilities/typeGuards/isIErrorPersonAlreadyConnectedToManagerInfo";
import { getFullName } from "../../utilities/getFullNameStringFromObject";

export const useAttachPersonToManager = () => {
  const currentManagerInfo = useAppSelector(
    (state) => state.adminManagerInfoSlice.currentManager,
  );
  const dispatch = useDispatch();

  const [reattachErrorManagerName, setReattachErrorManagerName] = useState<
    string | null
  >(null);

  const [reattachErrorModalOpen, setReattachErrorModalOpen] = useState(false);

  const errorConnectedPersonToManagerInfoRef =
    useRef<IErrorPersonAlreadyConnectedToManagerInfo | null>(null);
  const errorAlreadyConnectedPersonToManagerId = useRef<number | null>(null);
  const lastSendInnPersonForAttach = useRef<string | null>(null);

  const { onSubmitReattach, isLoading: isLoadingReattach } =
    useSubmitReattachToManager();

  const [attachManager, { isLoading: isLoadingAttach }] =
    useAttachToManagerMutation();

  const attachPersonFormFinish: FormProps["onFinish"] = async (value) => {
    const { inn } = value;
    if (currentManagerInfo && typeof inn === "string") {
      lastSendInnPersonForAttach.current = inn;
      try {
        if (
          !currentManagerInfo.agents.find((el) => el.inn === inn || el.organization.inn === inn) &&
          !currentManagerInfo?.clients?.find((el) => el.inn === inn || el.organization.inn === inn)
        ) {
          const response = await attachManager({
            managerId: currentManagerInfo?.id,
            inn: inn,
          }).unwrap();

          const personType = response.data.partner_id.type;

          const personData: IFixPersonToManager = {
            id: response.data.partner_id.id,
            organization_id: response.data.partner_id.organization_id,
            organization: response.data.partner_id.organization,
            role_id: response.data.partner_id.role_id,
            surname: response.data.partner_id.surname,
            name: response.data.partner_id.name,
            patronymic: response.data.partner_id.patronymic || "",
            email: response.data.partner_id.email,
            image_path: response.data.partner_id.image_path,
            inn: lastSendInnPersonForAttach.current,
            phone_number: response.data.partner_id.phone_number,
            type: personType,
            is_banned: response.data.partner_id.is_banned,
            is_enabled: response.data.partner_id.is_banned,
            position: response.data.partner_id.position,
            connect_data: new Date().toISOString(),
          };

          dispatch(
            attachPersonToManager({
              personType,
              personData,
            }),
          );
          setReattachErrorManagerName(null);
          setReattachErrorModalOpen(false);
        } else {
          message.error("Этот клиент/агент уже закреплен за менеджером");
        }
      } catch (error: any) {
        const personData = error?.data?.personData;
        const managerInfo = error?.data?.managerInfo;

        if (
          error.status === 400 &&
          isIAlreadyConnectedPersonManagerInfo(managerInfo) &&
          isIErrorPersonAlreadyConnectedToManagerInfo(personData)
        ) {
          setReattachErrorModalOpen(true);
          setReattachErrorManagerName(getFullName(managerInfo));
          errorConnectedPersonToManagerInfoRef.current = personData;
          errorAlreadyConnectedPersonToManagerId.current =
            error.data.managerInfo.id;

          return;
        }

        if (error?.data?.message) {
          message.error(error.data.message);
        }
      }
    }
  };
  const onHandleReattachPersonToCurrentManager = async () => {
    const personType = errorConnectedPersonToManagerInfoRef.current?.type;
    if (
      errorConnectedPersonToManagerInfoRef.current &&
      currentManagerInfo?.id &&
      (personType === "agent" || personType === "client") &&
      errorAlreadyConnectedPersonToManagerId.current
    ) {
      await onSubmitReattach({
        personIds: [errorConnectedPersonToManagerInfoRef.current.id],
        type: personType,
        toManagerId: currentManagerInfo?.id,
        fromManagerId: errorAlreadyConnectedPersonToManagerId.current,
        afterSuccessQuery: () => {
          if (errorConnectedPersonToManagerInfoRef.current) {
            const connectedPersonData: IFixPersonToManager = {
              id: errorConnectedPersonToManagerInfoRef.current.id,
              organization_id:
                errorConnectedPersonToManagerInfoRef.current?.organization_id,
              organization:
                errorConnectedPersonToManagerInfoRef.current.organization,
              role_id: errorConnectedPersonToManagerInfoRef.current?.role_id,
              surname: errorConnectedPersonToManagerInfoRef.current?.surname,
              name: errorConnectedPersonToManagerInfoRef.current?.name,
              patronymic:
                errorConnectedPersonToManagerInfoRef.current?.patronymic,
              email: errorConnectedPersonToManagerInfoRef.current?.email,
              image_path:
                errorConnectedPersonToManagerInfoRef.current.image_path,
              phone_number:
                errorConnectedPersonToManagerInfoRef.current.phone_number,
              type: errorConnectedPersonToManagerInfoRef.current.type,
              is_banned: errorConnectedPersonToManagerInfoRef.current.is_banned,
              is_enabled:
                errorConnectedPersonToManagerInfoRef.current.is_enabled,
              inn: lastSendInnPersonForAttach.current,
              position: null,
              connect_data: new Date().toISOString(),
            };

            dispatch(
              attachPersonToManager({
                personType,
                personData: connectedPersonData,
              }),
            );

            setReattachErrorModalOpen(false);
            setReattachErrorManagerName(null);
          }
        },
      });
    }
  };

  const handleCancelReattachModal = () => {
    setReattachErrorModalOpen(false);
    setReattachErrorManagerName(null);
  };

  return {
    reattachErrorManagerName,
    reattachErrorModalOpen,
    onHandleReattachPersonToCurrentManager,
    isLoadingReattach,
    isLoadingAttach,
    attachPersonFormFinish,
    handleCancelReattachModal,
  };
};
